import { useEffect, useRef, useState } from "react"
import {
  type Room,
  RoomEvent,
  type RemoteParticipant,
  type DataPacket_Kind,
} from "@workspace/livekit/client"
import { record } from "@rrweb/record"
import type { event } from "@rrweb/types"
import { streamEvent } from "@/lib/stream-event"
import {
  CursorPositionSchema,
  RemoteControlActionSchema,
  RemoteControlAnnotationSchema,
} from "@/schemas"
import { dispatchRemoteAction } from "@/lib/remote-control-dispatch"

interface CursorPos {
  id: string
  x: number
  y: number
  name: string
}

interface AnnotationStroke {
  id: string
  points: { x: number; y: number }[]
  color: string
  width: number
  createdAt: number
}

interface UseRemoteControlOptions {
  room: Room
}

function upsertMapEntry<K, V>(map: Map<K, V>, key: K, value: V): Map<K, V> {
  // Always return a new map instance so React state updates remain immutable.
  const next = new Map(map)
  next.set(key, value)
  return next
}

function removeMapEntry<K, V>(map: Map<K, V>, key: K): Map<K, V> {
  // Preserve referential equality when nothing changes.
  if (!map.has(key)) return map
  const next = new Map(map)
  next.delete(key)
  return next
}

function startRecording(room: Room) {
  return record({
    emit(event: event) {
      streamEvent(room, event)
    },
    inlineStylesheet: true,
    inlineImages: true,
    collectFonts: true,
    ignoreSelector:
      "[data-meridial-ui]:not([data-meridial-ui-allow-recording])",
  })
}

export function useRemoteControl({ room }: UseRemoteControlOptions) {
  const LASER_TRAIL_TTL_MS = 3000
  const PRUNE_INTERVAL_MS = 200

  // List of cursor positions of the support participants that have been granted remote control.
  const [cursorMap, setCursorMap] = useState<Map<string, CursorPos>>(
    () => new Map()
  )
  const [annotationMap, setAnnotationMap] = useState<
    Map<string, AnnotationStroke[]>
  >(() => new Map())

  const stopRecordingRef = useRef<ReturnType<typeof record> | undefined>(
    undefined
  )

  // -------------------------- Room Event handlers --------------------------

  function handleDataReceived(
    payload: Uint8Array,
    participant?: RemoteParticipant,
    _kind?: DataPacket_Kind,
    topic?: string
  ) {
    switch (topic) {
      case "remoteControl:cursor":
        const cursorPayload = JSON.parse(new TextDecoder().decode(payload))
        const cursorResult = CursorPositionSchema.safeParse(cursorPayload)

        if (!cursorResult.success || !participant) return
        const { x, y, name } = cursorResult.data
        setCursorMap((prev) => {
          if (x < 0 || y < 0) {
            // When cursor goes outside the viewport (-1, -1), we remove the cursor from the map.
            return removeMapEntry(prev, participant.identity)
          }

          // The cursor position is normalized to the range [0, 1], so we need to convert it to pixels.
          return upsertMapEntry(prev, participant.identity, {
            id: participant.identity,
            x: x * window.innerWidth,
            y: y * window.innerHeight,
            name,
          })
        })
        break
      case "remoteControl:action":
        if (!participant) return
        const actionPayload = JSON.parse(new TextDecoder().decode(payload))
        const actionResult = RemoteControlActionSchema.safeParse(actionPayload)
        if (!actionResult.success) return
        dispatchRemoteAction(actionResult.data)
        break
      case "remoteControl:annotation":
        if (!participant) return
        const annotationPayload = JSON.parse(new TextDecoder().decode(payload))
        const annotationResult =
          RemoteControlAnnotationSchema.safeParse(annotationPayload)
        if (!annotationResult.success) return
        if (annotationResult.data.type === "clear") break
        const annotation = annotationResult.data
        setAnnotationMap((prev) => {
          const current = prev.get(participant.identity) ?? []
          return upsertMapEntry(prev, participant.identity, [
            ...current,
            {
              id: `${participant.identity}:${annotation.timestamp}:${current.length}`,
              points: annotation.points,
              color: annotation.color,
              width: annotation.width,
              createdAt: annotation.timestamp,
            },
          ])
        })
        break
      case "replay:fullSnapshot":
        record.takeFullSnapshot()
        break
    }
  }

  function handleParticipantDisconnected(participant: RemoteParticipant) {
    // Participant leaving should clear all control-related state immediately.
    setCursorMap((prev) => removeMapEntry(prev, participant.identity))
    setAnnotationMap((prev) => removeMapEntry(prev, participant.identity))
  }

  function handleParticipantConnected(participant: RemoteParticipant) {
    let participantMetadata: { type?: string } = {}
    try {
      participantMetadata = JSON.parse(participant.metadata ?? "{}")
    } catch {
      participantMetadata = {}
    }

    // Start recording when support joins, but avoid creating duplicate recorders.
    if (participantMetadata.type === "SUPPORT" && !stopRecordingRef.current) {
      stopRecordingRef.current = startRecording(room)
    }
  }

  function handleRoomDisconnected() {
    // Stop recording when room is disconnected.
    if (stopRecordingRef.current) {
      stopRecordingRef.current()
      stopRecordingRef.current = undefined
    }
  }

  useEffect(() => {
    // Register event listeners.
    room.on(RoomEvent.DataReceived, handleDataReceived)
    room.on(RoomEvent.ParticipantConnected, handleParticipantConnected)
    room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected)
    room.on(RoomEvent.Disconnected, handleRoomDisconnected)
    return () => {
      // Clean up event listeners.
      room.off(RoomEvent.DataReceived, handleDataReceived)
      room.off(RoomEvent.ParticipantConnected, handleParticipantConnected)
      room.off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected)
      room.off(RoomEvent.Disconnected, handleRoomDisconnected)
      if (stopRecordingRef.current) {
        stopRecordingRef.current()
        stopRecordingRef.current = undefined
      }
    }
  }, [room])

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now()
      setAnnotationMap((prev) => {
        const next = new Map(prev)
        for (const [identity, annotations] of next.entries()) {
          next.set(
            identity,
            annotations.filter(
              (annotation) => now - annotation.createdAt < LASER_TRAIL_TTL_MS
            )
          )
        }
        return next
      })
    }, PRUNE_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [])

  return {
    cursors: Array.from(cursorMap.values()),
    annotations: Array.from(annotationMap.values()).flat(),
  }
}
